import {
  Box,
  Button,
  Badge,
  Fieldset,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  PinInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  rgba,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Form, useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  useActiveAuthProvider,
  useNotification,
  useTranslation,
} from '@refinedev/core';
import * as Sentry from '@sentry/react';
import {
  IconBan,
  IconCircleCheckFilled,
  IconDeviceMobile,
  IconDownload,
  IconLock,
  IconMessageCircle,
  IconSettings,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useListPayors, useUpdateMFA, useUpdateUser } from 'api/resource';
import { setMFAEnabled, setMFAPreference } from 'api/utils/auth';
import {
  fetchMFAPreference,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { TOTPSetup } from 'components/auth/TOTPSetup';
import { settingsSteps } from 'components/tutorial/Steps';
import { TutorialComponent } from 'components/tutorial/TutorialComponent';
import { useIsProvider } from 'hooks';
import { PayorType } from 'interfaces';
import type { User } from 'interfaces';
import { ClaimActionConfirmDialogModal } from 'pages/claims/components';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';
import {
  exportClaimDraftBackup,
  importClaimDraftBackup,
  useClaimDraftStore,
} from 'store/ClaimDraftStore';
import {
  type ChartType,
  type EditPageLayout,
  syncSettingsWithBackend,
  useSettingsStore,
} from 'store/SettingsStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useTutorialStore } from 'store/tutorialStore';
import { useCurrentUserStore } from 'store/UserStore';
import { nowISO } from 'utils/dateUtils';
import { fiscalYearEndMonthOptions } from 'utils/fiscalYear';
import { MyPreviousBatches } from './batches';

const SETTINGS_FIELDSET_STYLE = {
  backdropFilter: 'blur(20px)',
  boxShadow: 'var(--mantine-shadow-md)',
  background: 'transparent',
} as const;

type MFAMethod = 'none' | 'sms' | 'totp';

const CLAIM_DRAFT_BACKUP_FILE_TYPE = 'application/json';
const DEFAULT_PHONE_NUMBER = '+12045555555';
const MFA_PREFERENCE: Record<MFAMethod, string> = {
  none: 'NOMFA',
  sms: 'SMS',
  totp: 'TOTP',
};
const MFA_METHOD_ICONS = {
  none: IconLock,
  sms: IconMessageCircle,
  totp: IconDeviceMobile,
};

const getEnabledMFABackground = (color: string, surfaceColor: string) =>
  `linear-gradient(${rgba(color, 0.08)}, ${rgba(
    color,
    0.08
  )}), ${surfaceColor}`;

const getErrorName = (error: unknown): string => {
  if (typeof error === 'object' && error && 'name' in error) {
    return String(error.name);
  }
  return 'UnknownError';
};

const didAuthActionSucceed = (response: unknown): boolean =>
  typeof response === 'object' &&
  response !== null &&
  'success' in response &&
  response.success === true;

const resolveMFAMethod = ({
  preferred,
  enabled,
}: {
  preferred?: string;
  enabled: string[];
}): MFAMethod => {
  if (preferred === 'TOTP') return 'totp';
  if (preferred === 'SMS') return 'sms';
  if (enabled.includes('TOTP')) return 'totp';
  if (enabled.includes('SMS')) return 'sms';
  return 'none';
};

const formatClaimDraftBackupFilename = () =>
  `claim-drafts-${nowISO().replace(/[:.]/g, '-')}.json`;

export const Settings = () => {
  const { isTutorialActive, completeTutorial } = useTutorialStore();

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [previousBatchesModalOpen, setPreviousBatchesModalOpen] =
    useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [waitingForCode, setWaitingForCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [opened, { open: openPhoneModal, close }] = useDisclosure(false);
  const [
    totpSetupOpened,
    { open: openTOTPSetupModal, close: closeTOTPSetupModal },
  ] = useDisclosure(false);
  const [
    downloadClaimDraftModalOpen,
    { open: openDownloadClaimDraftModal, close: closeDownloadClaimDraftModal },
  ] = useDisclosure(false);
  const [
    clearClaimDraftModalOpen,
    { open: openClearClaimDraftModal, close: closeClearClaimDraftModal },
  ] = useDisclosure(false);
  const claimDraftImportRef = useRef<HTMLInputElement | null>(null);

  const { data: payors, isLoading } = useListPayors();

  const payorSelectOptions = payors?.reduce<
    Record<string, { value: string; label: string }[]>
  >((acc, payor) => {
    const group = payor.type;
    acc[group] = acc[group] || [];
    acc[group].push({ value: payor.id, label: payor.name });
    return acc;
  }, {});
  const groupedPayorSelectOptions = Object.entries(
    payorSelectOptions ?? {}
  ).map(([group, items]) => ({
    group,
    items: [...items].sort((a, b) => a.label.localeCompare(b.label)),
  }));

  const [mfaMethod, setMFAMethod] = useState<MFAMethod>('none');
  const [mfaLoading, setMFALoading] = useState(false);
  const [totpSecret, setTOTPSecret] = useState<string | null>(null);
  const [totpSetupError, setTOTPSetupError] = useState<string | null>(null);

  const user = useCurrentUserStore();
  const { province: currentSiteProvince } = useCurrentSiteStore();
  const updateUser = useUpdateUser();
  const updateUserMFA = useUpdateMFA();
  const authProvider = useActiveAuthProvider();

  const organizationSites = user?.organization?.sites ?? [];
  const isProvider = useIsProvider();
  const computedColorScheme = useComputedColorScheme('dark');
  const theme = useMantineTheme();
  const primaryThemeColor = `primary-${computedColorScheme}`;
  const accentThemeColor = `accent-${computedColorScheme}`;
  const greenThemeColor = `green-${computedColorScheme}`;
  const grayThemeColor = `gray-${computedColorScheme}`;
  const redThemeColor = `red-${computedColorScheme}`;
  const primaryColor = theme.colors[primaryThemeColor][0];
  const greenColor = theme.colors[greenThemeColor][0];
  const grayColor = theme.colors[grayThemeColor][0];
  const surfaceColor = theme.colors[`surface-${computedColorScheme}`][0];
  const borderColor = theme.colors[`border-${computedColorScheme}`][0];
  const isTOTPEnabled = mfaMethod === 'totp';
  const isSMSEnabled = mfaMethod === 'sms';
  const isMFADisabled = mfaMethod === 'none';
  const currentMFAAppearance = {
    none: {
      background: surfaceColor,
      borderColor,
      Icon: MFA_METHOD_ICONS.none,
      textColor: undefined,
      themeColor: grayThemeColor,
    },
    sms: {
      background: getEnabledMFABackground(primaryColor, surfaceColor),
      borderColor: primaryColor,
      Icon: MFA_METHOD_ICONS.sms,
      textColor: primaryThemeColor,
      themeColor: primaryThemeColor,
    },
    totp: {
      background: getEnabledMFABackground(greenColor, surfaceColor),
      borderColor: greenColor,
      Icon: MFA_METHOD_ICONS.totp,
      textColor: greenThemeColor,
      themeColor: greenThemeColor,
    },
  }[mfaMethod];

  const { translate, changeLocale } = useTranslation();
  const { i18n } = useI18nextTranslation();
  const currentLocale = i18n.language || 'en-CA';
  const currentMFAStatusTitle = translate(
    `pages:settings.security.status.${mfaMethod}.title`
  );
  const currentMFAStatusDescription = translate(
    `pages:settings.security.status.${mfaMethod}.description`
  );
  const authenticatorActionLabel = translate(
    isTOTPEnabled
      ? 'pages:settings.security.methods.totp.setupAgain'
      : 'pages:settings.security.methods.totp.enable'
  );
  const passwordResetActionLabel = translate(
    waitingForCode
      ? 'pages:settings.password.sendingCode'
      : 'pages:settings.password.sendResetCode'
  );
  const localeOptions = [
    {
      value: 'en-CA',
      label: translate('pages:settings.languages.english'),
    },
    {
      value: 'fr-CA',
      label: translate('pages:settings.languages.french'),
    },
  ];
  const chartTypeOptions = [
    {
      value: 'line',
      label: translate('pages:settings.chart.options.line'),
    },
    {
      value: 'bar',
      label: translate('pages:settings.chart.options.bar'),
    },
    {
      value: 'area',
      label: translate('pages:settings.chart.options.area'),
    },
  ];
  const editLayoutOptions = [
    {
      value: 'tabbed',
      label: translate('pages:settings.editLayout.options.tabbed'),
    },
    {
      value: 'unified',
      label: translate('pages:settings.editLayout.options.unified'),
    },
  ];

  // Settings store (persisted in session via Zustand)
  const {
    chartType,
    setChartType,
    editPageLayout,
    setEditPageLayout,
    defaultSiteID,
    setDefaultSiteID,
    fiscalYearEnd,
    setFiscalYearEnd,
    defaultPayorID,
    setDefaultPayorID,
  } = useSettingsStore();

  const configuredDefaultPayor = payors?.some(
    ({ id }) => id === defaultPayorID
  );
  const defaultPayorProvince =
    organizationSites.find(({ id }) => id === defaultSiteID)?.province ??
    currentSiteProvince;
  const resolvedDefaultPayorID = configuredDefaultPayor
    ? defaultPayorID
    : payors?.find(
        ({ province, type }) =>
          type === PayorType.PUBLIC && province === defaultPayorProvince
      )?.id ?? null;

  const { open } = useNotification();
  const clearAllClaimDrafts = useClaimDraftStore(
    (state) => state.clearAllDrafts
  );
  const form = useForm<User>({
    initialValues: user,
    validateInputOnChange: true,
    validate: {
      phone: (value) => {
        const phoneRegex =
          /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
        if (!phoneRegex.test(value)) {
          return translate('pages:settings.validation.invalidPhone');
        }
        return null;
      },
    },
  });
  const passwordForm = useForm({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validateInputOnChange: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (values.newPassword !== values.confirmPassword) {
        errors.confirmPassword = translate(
          'pages:settings.validation.passwordsDoNotMatch'
        );
      }
      return errors;
    },
  });

  const refreshMFAState = useCallback(async () => {
    const preference = await fetchMFAPreference();
    const preferred = preference.preferred?.toUpperCase();
    const enabled = (preference.enabled ?? []).map((method) =>
      method.toUpperCase()
    );
    const method = resolveMFAMethod({ preferred, enabled });

    setMFAMethod(method);
    setMFAPreference(MFA_PREFERENCE[method]);
    setMFAEnabled(method !== 'none');
  }, []);

  useEffect(() => {
    void refreshMFAState().catch((error) => {
      Sentry.captureException(
        new Error(`Unable to load MFA preference: ${getErrorName(error)}`)
      );
    });
    return () => {
      setTOTPSecret(null);
    };
  }, [refreshMFAState]);

  const closeTOTPSetup = () => {
    setTOTPSecret(null);
    setTOTPSetupError(null);
    closeTOTPSetupModal();
  };

  const startTOTPSetup = async () => {
    setMFALoading(true);
    setTOTPSetupError(null);
    try {
      const setup = await setUpTOTP();
      setTOTPSecret(setup.sharedSecret);
      openTOTPSetupModal();
    } catch (error) {
      Sentry.captureException(
        new Error(`Unable to start authenticator setup: ${getErrorName(error)}`)
      );
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.security.notifications.setupUnavailable'
        ),
      });
    } finally {
      setMFALoading(false);
    }
  };

  const verifyAndEnableTOTP = async (code: string) => {
    setMFALoading(true);
    setTOTPSetupError(null);
    try {
      await verifyTOTPSetup({ code });
      await updateMFAPreference({ totp: 'PREFERRED', sms: 'DISABLED' });
      await refreshMFAState();
      closeTOTPSetup();
      open?.({
        type: 'success',
        message: translate(
          'pages:settings.security.notifications.authenticatorEnabled'
        ),
      });
    } catch (error) {
      const errorName = getErrorName(error);
      Sentry.captureException(
        new Error(`Unable to verify authenticator setup: ${errorName}`)
      );
      const isInvalidCode =
        errorName === 'CodeMismatchException' ||
        errorName === 'EnableSoftwareTokenMFAException';
      setTOTPSetupError(
        translate(
          isInvalidCode
            ? 'pages:settings.security.errors.invalidOrExpiredCode'
            : 'pages:settings.security.errors.setupFailed'
        )
      );
    } finally {
      setMFALoading(false);
    }
  };

  const changeMFAMethod = async (method: 'none' | 'sms') => {
    if (method === 'sms' && form.values.phone === DEFAULT_PHONE_NUMBER) {
      openPhoneModal();
      return;
    }

    setMFALoading(true);
    try {
      if (method === 'sms' && form.values.phone !== user.phone) {
        await updateUser.mutateAsync(form.values);
      }
      if (method === 'sms') {
        await updateUserMFA.mutateAsync({
          method: 'sms',
          enabled: true,
          email: user.email,
          phone: form.values.phone,
        });
      } else {
        await updateUserMFA.mutateAsync({
          method: 'none',
          enabled: false,
          email: user.email,
        });
      }
      await refreshMFAState();
      open?.({
        type: 'success',
        message: translate(
          `pages:settings.security.notifications.${method}Enabled`
        ),
      });
    } catch (error) {
      Sentry.captureException(
        new Error(`Unable to update MFA preference: ${getErrorName(error)}`)
      );
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.security.notifications.updateFailed'
        ),
      });
    } finally {
      setMFALoading(false);
    }
  };

  const resetClaimDraftImport = () => {
    if (claimDraftImportRef.current) {
      claimDraftImportRef.current.value = '';
    }
  };

  const handleDownloadClaimDraftBackup = () => {
    const backup = exportClaimDraftBackup();

    if (!backup) {
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.claimDraftBackup.notifications.noneAvailable'
        ),
      });
      return;
    }

    try {
      const blob = new Blob([JSON.stringify(backup, null, 2)], {
        type: CLAIM_DRAFT_BACKUP_FILE_TYPE,
      });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = formatClaimDraftBackupFilename();
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);

      open?.({
        type: 'success',
        message: translate(
          'pages:settings.claimDraftBackup.notifications.downloadStarted'
        ),
      });
    } catch {
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.claimDraftBackup.notifications.downloadFailed'
        ),
      });
    } finally {
      closeDownloadClaimDraftModal();
    }
  };

  const handleImportClaimDraftBackup = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (
      file.type !== CLAIM_DRAFT_BACKUP_FILE_TYPE &&
      !file.name.toLowerCase().endsWith('.json')
    ) {
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.claimDraftBackup.notifications.jsonOnly'
        ),
      });
      resetClaimDraftImport();
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const content = reader.result;
        if (typeof content !== 'string') {
          throw new Error('Invalid file content');
        }

        importClaimDraftBackup(JSON.parse(content));
        open?.({
          type: 'success',
          message: translate(
            'pages:settings.claimDraftBackup.notifications.importSucceeded'
          ),
        });
      } catch {
        open?.({
          type: 'error',
          message: translate(
            'pages:settings.claimDraftBackup.notifications.invalidFile'
          ),
        });
      } finally {
        resetClaimDraftImport();
      }
    };

    reader.onerror = () => {
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.claimDraftBackup.notifications.readFailed'
        ),
      });
      resetClaimDraftImport();
    };

    reader.readAsText(file);
  };

  const handleClearClaimDraftBackup = () => {
    clearAllClaimDrafts();
    open?.({
      type: 'success',
      message: translate(
        'pages:settings.claimDraftBackup.notifications.clearSucceeded'
      ),
    });
  };

  const handleInitiateReset = async () => {
    setWaitingForCode(true);
    const response = await authProvider.forgotPassword({
      email: user.email,
    });
    if (didAuthActionSucceed(response)) {
      setNewPassword(passwordForm.values.newPassword);
      setResetModalOpen(true);
    } else {
      setWaitingForCode(false);
    }
  };

  const handleConfirmReset = async () => {
    const response = await authProvider.updatePassword({
      email: user.email,
      code: resetCode,
      newPassword,
    });
    if (didAuthActionSucceed(response)) {
      passwordForm.reset();
      open?.({
        type: 'success',
        message: translate(
          'pages:settings.password.notifications.updateSucceeded'
        ),
      });
    } else {
      open?.({
        type: 'error',
        message: translate(
          'pages:settings.password.notifications.updateFailed'
        ),
      });
    }
    setResetModalOpen(false);
    setWaitingForCode(false);
  };

  const handleSubmit = async (values: User) => {
    setLoading(true);
    await updateUser.mutateAsync(values);
    await syncSettingsWithBackend();
    setLoading(false);
  };

  return (
    <>
      {isTutorialActive && (
        <TutorialComponent
          steps={settingsSteps}
          onComplete={completeTutorial}
          page="settings"
          waitForSelector=".settings-page"
        />
      )}
      <Paper
        withBorder
        shadow="md"
        p="md"
        bg="transparent"
        className="settings-page"
      >
        <Title order={3} mb="md">
          {translate('pages:settings.pageTitle')}
        </Title>
        <Form form={form} onSubmit={handleSubmit}>
          <Fieldset
            radius="lg"
            mb="lg"
            style={SETTINGS_FIELDSET_STYLE}
            className="personal-info"
          >
            <Title order={5} mb="sm">
              {translate('pages:settings.personalInformation.title')}
            </Title>
            {isLoading ? (
              <Flex gap="md" mb="md" justify="center" align="center">
                <Loader />
              </Flex>
            ) : (
              <>
                <Flex gap="md" mb="md">
                  <TextInput
                    label={translate(
                      'pages:settings.personalInformation.firstName'
                    )}
                    placeholder={translate(
                      'pages:settings.personalInformation.firstName'
                    )}
                    style={{ flex: 1 }}
                    {...form.getInputProps('firstName')}
                  />
                  <TextInput
                    label={translate(
                      'pages:settings.personalInformation.lastName'
                    )}
                    placeholder={translate(
                      'pages:settings.personalInformation.lastName'
                    )}
                    style={{ flex: 1 }}
                    {...form.getInputProps('lastName')}
                  />
                </Flex>

                <Flex gap="md" mb="md">
                  <TextInput
                    label={translate(
                      'pages:settings.personalInformation.email'
                    )}
                    placeholder={translate(
                      'pages:settings.personalInformation.emailPlaceholder'
                    )}
                    style={{ flex: 1 }}
                    {...form.getInputProps('email')}
                  />
                  <TextInput
                    label={translate(
                      'pages:settings.personalInformation.phone'
                    )}
                    placeholder={translate(
                      'pages:settings.personalInformation.phonePlaceholder'
                    )}
                    style={{ flex: 1 }}
                    {...form.getInputProps('phone')}
                  />
                </Flex>

                <Flex gap="md" mb="md">
                  <TextInput
                    label={translate(
                      'pages:settings.personalInformation.organization'
                    )}
                    placeholder={translate(
                      'pages:settings.personalInformation.organizationPlaceholder'
                    )}
                    style={{ flex: 1 }}
                    disabled
                    value={user.organization?.name ?? ''}
                  />
                  <TextInput
                    label={translate('pages:settings.personalInformation.role')}
                    placeholder={translate(
                      'pages:settings.personalInformation.rolePlaceholder'
                    )}
                    style={{ flex: 1 }}
                    disabled
                    value={user.role?.name ?? ''}
                  />
                </Flex>
                <Flex gap="md" mb="md">
                  {isProvider && (
                    <Select
                      label={translate(
                        'pages:settings.personalInformation.fiscalYearEnd'
                      )}
                      placeholder={translate(
                        'pages:settings.personalInformation.fiscalYearEndPlaceholder'
                      )}
                      data={fiscalYearEndMonthOptions}
                      value={String(fiscalYearEnd ?? 12)}
                      onChange={(val) => {
                        if (val) {
                          setFiscalYearEnd(Number(val));
                        }
                      }}
                      mt="md"
                      style={{ flex: 1 }}
                    />
                  )}
                  <Select
                    label={translate(
                      'pages:settings.personalInformation.defaultSite'
                    )}
                    placeholder={translate(
                      'pages:settings.personalInformation.defaultSitePlaceholder'
                    )}
                    data={organizationSites.map((site) => ({
                      value: site.id,
                      label: site.name,
                    }))}
                    value={defaultSiteID}
                    onChange={(val) => {
                      setDefaultSiteID(val);
                    }}
                    mt="md"
                    style={{ flex: 1 }}
                  />
                </Flex>
                <Flex gap="md" mb="md">
                  {payors && (
                    <Select
                      label={translate(
                        'pages:settings.personalInformation.defaultPayor'
                      )}
                      placeholder={translate(
                        'pages:settings.personalInformation.defaultPayorPlaceholder'
                      )}
                      data={groupedPayorSelectOptions}
                      value={resolvedDefaultPayorID}
                      onChange={(val) => {
                        setDefaultPayorID(val);
                      }}
                      disabled={isLoading}
                      mt="md"
                      style={{ flex: 1 }}
                    />
                  )}
                </Flex>
                <Button type="submit" mt="sm" loading={loading}>
                  {translate('pages:settings.personalInformation.save')}
                </Button>
              </>
            )}
          </Fieldset>
        </Form>

        <Fieldset
          radius="lg"
          mb="lg"
          style={SETTINGS_FIELDSET_STYLE}
          className="security-settings"
        >
          <Group justify="space-between" align="flex-start" mb="md">
            <Group gap="sm" align="flex-start">
              <Box>
                <Title order={5}>
                  {translate('pages:settings.security.title')}
                </Title>
                <Text size="sm" c="dimmed" mt={2}>
                  {translate('pages:settings.security.description')}
                </Text>
              </Box>
            </Group>
          </Group>

          <Paper
            withBorder
            radius="md"
            p="md"
            mb="lg"
            style={{
              background: currentMFAAppearance.background,
              borderColor: currentMFAAppearance.borderColor,
            }}
          >
            <Group gap="md" wrap="nowrap">
              <ThemeIcon
                size={44}
                radius="xl"
                variant="light"
                color={currentMFAAppearance.themeColor}
                aria-hidden
              >
                <currentMFAAppearance.Icon size={24} stroke={1.7} />
              </ThemeIcon>
              <Box>
                <Text size="sm" fw={700} c={currentMFAAppearance.textColor}>
                  {currentMFAStatusTitle}
                </Text>
                <Text size="sm" c="dimmed" mt={2}>
                  {currentMFAStatusDescription}
                </Text>
              </Box>
            </Group>
          </Paper>

          <Title order={5} mb="md">
            {translate('pages:settings.security.manageTitle')}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Paper
              withBorder
              radius="md"
              style={{
                minWidth: 0,
                overflow: 'hidden',
                background: surfaceColor,
                borderColor: isTOTPEnabled ? greenColor : borderColor,
                boxShadow: isTOTPEnabled
                  ? `0 0 0 1px ${greenColor}`
                  : undefined,
              }}
            >
              <Stack h="100%" gap={0}>
                <Box p="md" mih={{ base: 'auto', md: 155 }} style={{ flex: 1 }}>
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <Group gap="md" align="flex-start" wrap="nowrap">
                      <ThemeIcon
                        size={38}
                        radius="xl"
                        variant="light"
                        color={greenThemeColor}
                        aria-hidden
                      >
                        <IconDeviceMobile size={21} />
                      </ThemeIcon>
                      <Box>
                        <Group gap="xs">
                          <Text fw={700}>
                            {translate(
                              'pages:settings.security.methods.totp.title'
                            )}
                          </Text>
                          {isTOTPEnabled && (
                            <Badge
                              color={greenThemeColor}
                              variant="light"
                              size="sm"
                            >
                              {translate(
                                'pages:settings.security.currentBadge'
                              )}
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" c="dimmed" mt="sm">
                          {translate(
                            'pages:settings.security.methods.totp.description'
                          )}
                        </Text>
                      </Box>
                    </Group>
                    {isTOTPEnabled && (
                      <IconCircleCheckFilled
                        size={24}
                        aria-label={translate(
                          'pages:settings.security.selectedMethodAriaLabel'
                        )}
                        style={{ flex: '0 0 auto', color: greenColor }}
                      />
                    )}
                  </Group>
                </Box>
                <Button
                  variant="subtle"
                  color={greenThemeColor}
                  radius={0}
                  style={{
                    minHeight: 44,
                    borderTop: `1px solid ${borderColor}`,
                  }}
                  leftSection={
                    isTOTPEnabled ? <IconSettings size={18} /> : undefined
                  }
                  loading={mfaLoading}
                  onClick={startTOTPSetup}
                >
                  {authenticatorActionLabel}
                </Button>
              </Stack>
            </Paper>

            <Paper
              withBorder
              radius="md"
              style={{
                minWidth: 0,
                overflow: 'hidden',
                background: surfaceColor,
                borderColor: isSMSEnabled ? primaryColor : borderColor,
                boxShadow: isSMSEnabled
                  ? `0 0 0 1px ${primaryColor}`
                  : undefined,
              }}
            >
              <Stack h="100%" gap={0}>
                <Box p="md" mih={{ base: 'auto', md: 155 }} style={{ flex: 1 }}>
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <Group gap="md" align="flex-start" wrap="nowrap">
                      <ThemeIcon
                        size={38}
                        radius="xl"
                        variant="light"
                        color={primaryThemeColor}
                        aria-hidden
                      >
                        <IconMessageCircle size={21} />
                      </ThemeIcon>
                      <Box>
                        <Group gap="xs">
                          <Text fw={700}>
                            {translate(
                              'pages:settings.security.methods.sms.title'
                            )}
                          </Text>
                          {isSMSEnabled && (
                            <Badge
                              color={primaryThemeColor}
                              variant="light"
                              size="sm"
                            >
                              {translate(
                                'pages:settings.security.currentBadge'
                              )}
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" c="dimmed" mt="sm">
                          {translate(
                            'pages:settings.security.methods.sms.description'
                          )}
                        </Text>
                      </Box>
                    </Group>
                    {isSMSEnabled && (
                      <IconCircleCheckFilled
                        size={24}
                        aria-label={translate(
                          'pages:settings.security.selectedMethodAriaLabel'
                        )}
                        style={{ flex: '0 0 auto', color: primaryColor }}
                      />
                    )}
                  </Group>
                </Box>
                <Button
                  variant="subtle"
                  radius={0}
                  style={{
                    minHeight: 44,
                    borderTop: `1px solid ${borderColor}`,
                  }}
                  loading={mfaLoading}
                  onClick={() => changeMFAMethod('sms')}
                >
                  {translate('pages:settings.security.methods.sms.enable')}
                </Button>
              </Stack>
            </Paper>

            <Paper
              withBorder
              radius="md"
              style={{
                minWidth: 0,
                overflow: 'hidden',
                background: surfaceColor,
                borderColor: isMFADisabled ? grayColor : borderColor,
                boxShadow: isMFADisabled ? `0 0 0 1px ${grayColor}` : undefined,
              }}
            >
              <Stack h="100%" gap={0}>
                <Box p="md" mih={{ base: 'auto', md: 155 }} style={{ flex: 1 }}>
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <Group gap="md" align="flex-start" wrap="nowrap">
                      <ThemeIcon
                        size={38}
                        radius="xl"
                        variant="light"
                        color={redThemeColor}
                        aria-hidden
                      >
                        <IconBan size={21} />
                      </ThemeIcon>
                      <Box>
                        <Group gap="xs">
                          <Text fw={700}>
                            {translate(
                              'pages:settings.security.methods.none.title'
                            )}
                          </Text>
                          {isMFADisabled && (
                            <Badge
                              color={grayThemeColor}
                              variant="light"
                              size="sm"
                            >
                              {translate(
                                'pages:settings.security.currentBadge'
                              )}
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" c="dimmed" mt="sm">
                          {translate(
                            'pages:settings.security.methods.none.description'
                          )}
                        </Text>
                      </Box>
                    </Group>
                  </Group>
                </Box>
                <Button
                  color={redThemeColor}
                  variant="subtle"
                  radius={0}
                  style={{
                    minHeight: 44,
                    borderTop: `1px solid ${borderColor}`,
                  }}
                  disabled={isMFADisabled}
                  loading={mfaLoading}
                  onClick={() => changeMFAMethod('none')}
                >
                  {translate('pages:settings.security.methods.none.disable')}
                </Button>
              </Stack>
            </Paper>
          </SimpleGrid>

          <Group gap="sm" mt="xl" c="dimmed" wrap="nowrap" align="flex-start">
            <IconLock size={17} aria-hidden />
            <Text size="xs">
              {translate('pages:settings.security.disableNote')}
            </Text>
          </Group>
        </Fieldset>

        {!isProvider && (
          <>
            <Fieldset
              radius="lg"
              style={SETTINGS_FIELDSET_STYLE}
              className="claim-draft-backup"
            >
              <Title order={5} mb="sm">
                {translate('pages:settings.claimDraftBackup.title')}
              </Title>
              <Text size="sm" c="dimmed" mb="md">
                {translate('pages:settings.claimDraftBackup.description')}
              </Text>
              <Flex gap="md" align="stretch" wrap="wrap">
                <Paper
                  withBorder
                  radius="md"
                  p="md"
                  style={{
                    flex: '1 1 220px',
                    minWidth: 220,
                    background: surfaceColor,
                  }}
                >
                  <Title order={6} mb={4}>
                    {translate(
                      'pages:settings.claimDraftBackup.download.title'
                    )}
                  </Title>
                  <Text size="sm" c="dimmed" mb="md">
                    {translate(
                      'pages:settings.claimDraftBackup.download.description'
                    )}
                  </Text>
                  <Button
                    fullWidth
                    radius="md"
                    variant="light"
                    leftSection={<IconDownload size={16} />}
                    onClick={openDownloadClaimDraftModal}
                  >
                    {translate(
                      'pages:settings.claimDraftBackup.download.action'
                    )}
                  </Button>
                </Paper>
                <Paper
                  withBorder
                  radius="md"
                  p="md"
                  style={{
                    flex: '1 1 220px',
                    minWidth: 220,
                    background: surfaceColor,
                  }}
                >
                  <Title order={6} mb={4}>
                    {translate('pages:settings.claimDraftBackup.restore.title')}
                  </Title>
                  <Text size="sm" c="dimmed" mb="md">
                    {translate(
                      'pages:settings.claimDraftBackup.restore.description'
                    )}
                  </Text>
                  <Button
                    fullWidth
                    radius="md"
                    variant="light"
                    color={accentThemeColor}
                    leftSection={<IconUpload size={16} />}
                    onClick={() => claimDraftImportRef.current?.click()}
                  >
                    {translate(
                      'pages:settings.claimDraftBackup.restore.action'
                    )}
                  </Button>
                </Paper>
                <Paper
                  withBorder
                  radius="md"
                  p="md"
                  style={{
                    flex: '1 1 220px',
                    minWidth: 220,
                    background: surfaceColor,
                  }}
                >
                  <Title order={6} mb={4}>
                    {translate('pages:settings.claimDraftBackup.clear.title')}
                  </Title>
                  <Text size="sm" c="dimmed" mb="md">
                    {translate(
                      'pages:settings.claimDraftBackup.clear.description'
                    )}
                  </Text>
                  <Button
                    fullWidth
                    radius="md"
                    variant="light"
                    color={redThemeColor}
                    leftSection={<IconTrash size={16} />}
                    onClick={openClearClaimDraftModal}
                  >
                    {translate('pages:settings.claimDraftBackup.clear.action')}
                  </Button>
                </Paper>
                <input
                  ref={claimDraftImportRef}
                  type="file"
                  accept={`${CLAIM_DRAFT_BACKUP_FILE_TYPE},.json`}
                  onChange={handleImportClaimDraftBackup}
                  data-testid="claim-draft-backup-upload-input"
                  style={{ display: 'none' }}
                />
              </Flex>
            </Fieldset>
            <br />
          </>
        )}

        <Form form={passwordForm} onSubmit={handleInitiateReset}>
          <Fieldset
            radius="lg"
            style={SETTINGS_FIELDSET_STYLE}
            className="password-reset"
          >
            <Title order={5} mb="sm">
              {translate('pages:settings.password.title')}
            </Title>
            <TextInput
              label={translate('pages:settings.password.newPassword')}
              type="password"
              {...passwordForm.getInputProps('newPassword')}
              mt="sm"
            />
            <TextInput
              label={translate('pages:settings.password.confirmPassword')}
              type="password"
              {...passwordForm.getInputProps('confirmPassword')}
              mt="md"
            />
            <Button type="submit" mt="md" disabled={waitingForCode}>
              {passwordResetActionLabel}
            </Button>
          </Fieldset>
        </Form>
        <br />

        <Fieldset
          radius="lg"
          style={SETTINGS_FIELDSET_STYLE}
          className="locale-preference"
        >
          <Title order={5} mb="sm">
            {translate('pages:settings.localePreference')}
          </Title>
          <Select
            label={translate('pages:settings.preferredLanguage')}
            placeholder={translate('pages:settings.selectLanguage')}
            data={localeOptions}
            value={currentLocale}
            onChange={(val) => {
              if (val) {
                changeLocale?.(val);
              }
            }}
            mt="md"
            style={{ flex: 1 }}
          />
        </Fieldset>
        <br />
        <Fieldset
          radius="lg"
          style={SETTINGS_FIELDSET_STYLE}
          className="dashboard-chart-preference"
        >
          <Title order={5} mb="sm">
            {translate('pages:settings.chart.title')}
          </Title>
          <SegmentedControl
            value={chartType}
            onChange={(value) => setChartType(value as ChartType)}
            data={chartTypeOptions}
          />
        </Fieldset>
        <br />
        <Fieldset
          radius="lg"
          style={SETTINGS_FIELDSET_STYLE}
          className="edit-layout-preference"
        >
          <Title order={5} mb="sm">
            {translate('pages:settings.editLayout.title')}
          </Title>
          <SegmentedControl
            value={editPageLayout}
            onChange={(value) => setEditPageLayout(value as EditPageLayout)}
            data={editLayoutOptions}
          />
        </Fieldset>
        <br />
        <Fieldset
          radius="lg"
          style={SETTINGS_FIELDSET_STYLE}
          className="previous-batches"
        >
          <Title order={5} mb="sm">
            {translate('pages:settings.batches.title')}
          </Title>
          <Text size="sm" c="dimmed" mb="md">
            {translate('pages:settings.batches.description')}
          </Text>
          <Button onClick={() => setPreviousBatchesModalOpen(true)}>
            {translate('pages:settings.batches.action')}
          </Button>
        </Fieldset>
        <br />

        <Modal
          opened={totpSetupOpened}
          onClose={closeTOTPSetup}
          title={translate('pages:settings.security.setupModalTitle')}
          centered
          closeOnClickOutside={!mfaLoading}
          closeOnEscape={!mfaLoading}
        >
          {totpSecret && (
            <TOTPSetup
              email={user.email}
              secret={totpSecret}
              error={totpSetupError}
              submitting={mfaLoading}
              onCancel={closeTOTPSetup}
              onVerify={verifyAndEnableTOTP}
            />
          )}
        </Modal>
        <Modal
          opened={previousBatchesModalOpen}
          onClose={() => setPreviousBatchesModalOpen(false)}
          title={translate('pages:settings.batches.modalTitle')}
          size="xl"
          centered
        >
          <MyPreviousBatches />
        </Modal>
        <Modal
          opened={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
          title={translate('pages:settings.password.verificationModalTitle')}
        >
          <PinInput
            length={6}
            type="number"
            value={resetCode}
            onChange={setResetCode}
            oneTimeCode
          />
          <Group align="right" mt="md">
            <Button onClick={handleConfirmReset}>
              {translate('pages:settings.actions.confirm')}
            </Button>
          </Group>
        </Modal>
        <Modal
          opened={opened}
          onClose={close}
          centered
          size="lg"
          withCloseButton
        >
          <Title order={5} mb="sm">
            {translate('pages:settings.phoneConfirmation.title')}
          </Title>
          <TextInput
            label={translate('pages:settings.phoneConfirmation.label')}
            placeholder={translate(
              'pages:settings.personalInformation.phonePlaceholder'
            )}
            value={form.values.phone}
            error={form.errors.phone}
            onChange={(event) => {
              form.setFieldValue('phone', event.currentTarget.value);
            }}
          />
          <Flex gap="md" mb="md" justify="center">
            <Button
              type="button"
              color={grayThemeColor}
              c={grayThemeColor}
              onClick={() => {
                close();
              }}
              mt="md"
            >
              {translate('pages:settings.actions.cancel')}
            </Button>
            <Button
              type="button"
              loading={mfaLoading}
              onClick={async () => {
                if (form.validateField('phone').hasError) {
                  return;
                }
                await changeMFAMethod('sms');
                close();
              }}
              mt="md"
              color={greenThemeColor}
            >
              {translate('pages:settings.actions.confirm')}
            </Button>
          </Flex>
        </Modal>
        <ClaimActionConfirmDialogModal
          open={downloadClaimDraftModalOpen}
          setOpen={(isOpen) => {
            if (isOpen) openDownloadClaimDraftModal();
            else closeDownloadClaimDraftModal();
          }}
          onConfirm={handleDownloadClaimDraftBackup}
          title={translate('pages:claims.exportDisclaimerTitle')}
          emphasizePhrase={translate('pages:claims.exportDisclaimerHighlight')}
          body={translate('pages:claims.exportDisclaimer')}
        />
        <ClaimActionConfirmDialogModal
          open={clearClaimDraftModalOpen}
          setOpen={(isOpen) => {
            if (isOpen) openClearClaimDraftModal();
            else closeClearClaimDraftModal();
          }}
          onConfirm={handleClearClaimDraftBackup}
          title={translate(
            'pages:settings.claimDraftBackup.clear.confirmTitle'
          )}
          body={translate('pages:settings.claimDraftBackup.clear.confirmBody')}
          confirmText={translate(
            'pages:settings.claimDraftBackup.clear.confirmAction'
          )}
          confirmButtonColor={redThemeColor}
        />
      </Paper>
    </>
  );
};
